import React, { useState, useEffect } from "react";
import { manageKeywordApi } from "../../../entities/keyword";
import { manageCategoryApi } from "../../../entities/category";
import { Keyword } from "../../../entities/keyword";
import { Category } from "../../../entities/category";
import "./KeywordManagement.scss";

const KeywordManagement: React.FC = () => {
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [sortBy, setSortBy] = useState<"nameKo" | "createdAt" | "category">(
    "createdAt",
  );
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedKeywords, setSelectedKeywords] = useState<string[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [keywordToDelete, setKeywordToDelete] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingKeyword, setEditingKeyword] = useState<Keyword | null>(null);

  // 폼 상태
  const [formData, setFormData] = useState({
    nameKo: "",
    nameEn: "",
    descKo: "",
    descEn: "",
    studyLinks: [""],
    categoryId: "",
    numOfArticles: 0,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [keywordsData, categoriesData] = await Promise.all([
        manageKeywordApi.getKeywordList(),
        manageCategoryApi.getCategoryList(),
      ]);
      setKeywords(keywordsData);
      setCategories(categoriesData);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteKeyword = async (id: string) => {
    try {
      await manageKeywordApi.deleteKeyword(id);
      setKeywords(keywords.filter((keyword) => keyword.id !== id));
      setShowDeleteModal(false);
      setKeywordToDelete(null);
    } catch (error) {
      console.error("Failed to delete keyword:", error);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(
        selectedKeywords.map((id) => manageKeywordApi.deleteKeyword(id)),
      );
      setKeywords(
        keywords.filter((keyword) => !selectedKeywords.includes(keyword.id)),
      );
      setSelectedKeywords([]);
    } catch (error) {
      console.error("Failed to delete keywords:", error);
    }
  };

  const handleAddKeyword = async () => {
    try {
      await manageKeywordApi.addKeyword(formData);
      // 새로 추가된 키워드를 목록에 추가하기 위해 전체 목록을 다시 가져옴
      await fetchData();
      setShowAddModal(false);
      resetForm();
    } catch (error) {
      console.error("Failed to add keyword:", error);
    }
  };

  const handleUpdateKeyword = async () => {
    if (!editingKeyword) return;

    try {
      await manageKeywordApi.updateKeyword(editingKeyword.id, formData);
      // 업데이트된 키워드를 목록에 반영하기 위해 전체 목록을 다시 가져옴
      await fetchData();
      setShowEditModal(false);
      setEditingKeyword(null);
      resetForm();
    } catch (error) {
      console.error("Failed to update keyword:", error);
    }
  };

  const openEditModal = (keyword: Keyword) => {
    setEditingKeyword(keyword);
    setFormData({
      nameKo: keyword.nameKo,
      nameEn: keyword.nameEn || "",
      descKo: keyword.descKo,
      descEn: keyword.descEn,
      studyLinks: keyword.studyLinks.length > 0 ? keyword.studyLinks : [""],
      categoryId: keyword.categoryId,
      numOfArticles: keyword.numOfArticles,
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      nameKo: "",
      nameEn: "",
      descKo: "",
      descEn: "",
      studyLinks: [""],
      categoryId: "",
      numOfArticles: 0,
    });
  };

  const addStudyLink = () => {
    setFormData({
      ...formData,
      studyLinks: [...formData.studyLinks, ""],
    });
  };

  const removeStudyLink = (index: number) => {
    setFormData({
      ...formData,
      studyLinks: formData.studyLinks.filter((_, i) => i !== index),
    });
  };

  const updateStudyLink = (index: number, value: string) => {
    const newStudyLinks = [...formData.studyLinks];
    newStudyLinks[index] = value;
    setFormData({
      ...formData,
      studyLinks: newStudyLinks,
    });
  };

  const filteredKeywords = keywords.filter((keyword) => {
    const matchesSearch =
      keyword.nameKo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (keyword.nameEn &&
        keyword.nameEn.toLowerCase().includes(searchTerm.toLowerCase())) ||
      keyword.descKo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      keyword.descEn.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory =
      !selectedCategory || keyword.categoryId === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedKeywords = [...filteredKeywords].sort((a, b) => {
    let aValue, bValue;

    switch (sortBy) {
      case "nameKo":
        aValue = a.nameKo;
        bValue = b.nameKo;
        break;
      case "createdAt":
        aValue = new Date(a.createdAt);
        bValue = new Date(b.createdAt);
        break;
      case "category":
        aValue = a.categoryNameKo;
        bValue = b.categoryNameKo;
        break;
      default:
        return 0;
    }

    if (sortOrder === "asc") {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  const totalPages = Math.ceil(sortedKeywords.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedKeywords = sortedKeywords.slice(
    startIndex,
    startIndex + itemsPerPage,
  );

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedKeywords(paginatedKeywords.map((keyword) => keyword.id));
    } else {
      setSelectedKeywords([]);
    }
  };

  const handleSelectKeyword = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedKeywords([...selectedKeywords, id]);
    } else {
      setSelectedKeywords(
        selectedKeywords.filter((keywordId) => keywordId !== id),
      );
    }
  };

  if (loading) {
    return (
      <div className="keyword-management">
        <div className="keyword-management__header">
          <h1>키워드 관리</h1>
          <p>키워드를 관리하고 편집하세요.</p>
        </div>
        <div className="loading">로딩 중...</div>
      </div>
    );
  }

  return (
    <div className="keyword-management">
      <div className="keyword-management__header">
        <h1>키워드 관리</h1>
        <p>키워드를 관리하고 편집하세요.</p>
      </div>

      {/* 검색 및 필터 */}
      <div className="keyword-management__filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="키워드 검색..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-box">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <option value="">모든 카테고리</option>
            {categories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.nameKo}
              </option>
            ))}
          </select>
        </div>
        <div className="sort-box">
          <select
            value={sortBy}
            onChange={(e) =>
              setSortBy(e.target.value as "nameKo" | "createdAt" | "category")
            }
          >
            <option value="nameKo">이름순</option>
            <option value="createdAt">생성일순</option>
            <option value="category">카테고리순</option>
          </select>
          <select
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
          >
            <option value="asc">오름차순</option>
            <option value="desc">내림차순</option>
          </select>
        </div>
      </div>

      {/* 액션 버튼 */}
      <div className="keyword-management__actions">
        <button
          className="btn btn-primary"
          onClick={() => {
            resetForm();
            setShowAddModal(true);
          }}
        >
          키워드 추가
        </button>
        {selectedKeywords.length > 0 && (
          <button
            className="btn btn-danger"
            onClick={() => setShowDeleteModal(true)}
          >
            선택 삭제 ({selectedKeywords.length})
          </button>
        )}
      </div>

      {/* 키워드 테이블 */}
      <div className="keyword-management__table">
        <table>
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  checked={
                    selectedKeywords.length === paginatedKeywords.length &&
                    paginatedKeywords.length > 0
                  }
                  onChange={(e) => handleSelectAll(e.target.checked)}
                />
              </th>
              <th>한국어 이름</th>
              <th>영어 이름</th>
              <th>카테고리</th>
              <th>설명 (한국어)</th>
              <th>설명 (영어)</th>
              <th>관련 아티클 수</th>
              <th>생성일</th>
              <th>액션</th>
            </tr>
          </thead>
          <tbody>
            {paginatedKeywords.map((keyword) => (
              <tr key={keyword.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedKeywords.includes(keyword.id)}
                    onChange={(e) =>
                      handleSelectKeyword(keyword.id, e.target.checked)
                    }
                  />
                </td>
                <td>{keyword.nameKo}</td>
                <td>{keyword.nameEn || "-"}</td>
                <td>{keyword.categoryNameKo}</td>
                <td className="description-cell">
                  {keyword.descKo.length > 50
                    ? `${keyword.descKo.substring(0, 50)}...`
                    : keyword.descKo}
                </td>
                <td className="description-cell">
                  {keyword.descEn.length > 50
                    ? `${keyword.descEn.substring(0, 50)}...`
                    : keyword.descEn}
                </td>
                <td>{keyword.numOfArticles}</td>
                <td>{new Date(keyword.createdAt).toLocaleDateString()}</td>
                <td>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => openEditModal(keyword)}
                  >
                    수정
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    onClick={() => {
                      setKeywordToDelete(keyword.id);
                      setShowDeleteModal(true);
                    }}
                  >
                    삭제
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 페이지네이션 */}
      <div className="keyword-management__pagination">
        <button
          disabled={currentPage === 1}
          onClick={() => setCurrentPage(currentPage - 1)}
        >
          이전
        </button>
        <span>
          {currentPage} / {totalPages}
        </span>
        <button
          disabled={currentPage === totalPages}
          onClick={() => setCurrentPage(currentPage + 1)}
        >
          다음
        </button>
      </div>

      {/* 키워드 추가 모달 */}
      {showAddModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>키워드 추가</h3>
              <button onClick={() => setShowAddModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>한국어 이름 *</label>
                <input
                  type="text"
                  value={formData.nameKo}
                  onChange={(e) =>
                    setFormData({ ...formData, nameKo: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>영어 이름</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData({ ...formData, nameEn: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>카테고리 *</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) =>
                    setFormData({ ...formData, categoryId: e.target.value })
                  }
                  required
                >
                  <option value="">카테고리 선택</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.nameKo}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>한국어 설명 *</label>
                <textarea
                  value={formData.descKo}
                  onChange={(e) =>
                    setFormData({ ...formData, descKo: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>영어 설명 *</label>
                <textarea
                  value={formData.descEn}
                  onChange={(e) =>
                    setFormData({ ...formData, descEn: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>관련 아티클 수</label>
                <input
                  type="number"
                  value={formData.numOfArticles}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numOfArticles: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>스터디 링크</label>
                {formData.studyLinks.map((link, index) => (
                  <div key={index} className="study-link-input">
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => updateStudyLink(index, e.target.value)}
                      placeholder="https://example.com"
                    />
                    {formData.studyLinks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStudyLink(index)}
                        className="btn btn-sm btn-danger"
                      >
                        삭제
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addStudyLink}
                  className="btn btn-sm btn-secondary"
                >
                  링크 추가
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowAddModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleAddKeyword}
                disabled={
                  !formData.nameKo ||
                  !formData.categoryId ||
                  !formData.descKo ||
                  !formData.descEn
                }
              >
                추가
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 키워드 수정 모달 */}
      {showEditModal && editingKeyword && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>키워드 수정</h3>
              <button onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>한국어 이름 *</label>
                <input
                  type="text"
                  value={formData.nameKo}
                  onChange={(e) =>
                    setFormData({ ...formData, nameKo: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>영어 이름</label>
                <input
                  type="text"
                  value={formData.nameEn}
                  onChange={(e) =>
                    setFormData({ ...formData, nameEn: e.target.value })
                  }
                />
              </div>
              <div className="form-group">
                <label>카테고리 *</label>
                <select
                  value={formData.categoryId}
                  onChange={(e) =>
                    setFormData({ ...formData, categoryId: e.target.value })
                  }
                  required
                >
                  <option value="">카테고리 선택</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.nameKo}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>한국어 설명 *</label>
                <textarea
                  value={formData.descKo}
                  onChange={(e) =>
                    setFormData({ ...formData, descKo: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>영어 설명 *</label>
                <textarea
                  value={formData.descEn}
                  onChange={(e) =>
                    setFormData({ ...formData, descEn: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>관련 아티클 수</label>
                <input
                  type="number"
                  value={formData.numOfArticles}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      numOfArticles: parseInt(e.target.value) || 0,
                    })
                  }
                />
              </div>
              <div className="form-group">
                <label>스터디 링크</label>
                {formData.studyLinks.map((link, index) => (
                  <div key={index} className="study-link-input">
                    <input
                      type="url"
                      value={link}
                      onChange={(e) => updateStudyLink(index, e.target.value)}
                      placeholder="https://example.com"
                    />
                    {formData.studyLinks.length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeStudyLink(index)}
                        className="btn btn-sm btn-danger"
                      >
                        삭제
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={addStudyLink}
                  className="btn btn-sm btn-secondary"
                >
                  링크 추가
                </button>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowEditModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateKeyword}
                disabled={
                  !formData.nameKo ||
                  !formData.categoryId ||
                  !formData.descKo ||
                  !formData.descEn
                }
              >
                수정
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 모달 */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>삭제 확인</h3>
              <button onClick={() => setShowDeleteModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {keywordToDelete ? (
                <p>선택한 키워드를 삭제하시겠습니까?</p>
              ) : (
                <p>
                  선택한 {selectedKeywords.length}개의 키워드를
                  삭제하시겠습니까?
                </p>
              )}
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-secondary"
                onClick={() => setShowDeleteModal(false)}
              >
                취소
              </button>
              <button
                className="btn btn-danger"
                onClick={() => {
                  if (keywordToDelete) {
                    handleDeleteKeyword(keywordToDelete);
                  } else {
                    handleBulkDelete();
                  }
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default KeywordManagement;
